import { IRoomDto } from '@dto';
import {
  HubConnectionBuilder,
  HubConnection,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import { trackSignalR } from 'utils/ga';

export interface ISocketMessage {
  data: string;
}

export interface ISocketMessageParsedData {
  code: number;
  data: IRoomDto;
  msg: string;
  type: SIGNALR_EVENTS;
}

const MAX_ATTEMPTS = 3;

class AppSignalR {
  pingInterval: NodeJS.Timer | undefined = undefined;
  connection: HubConnection | undefined = undefined;
  attempts: number = 0;
  isLoading: boolean = false;
  userId: any;

  constructor() {}

  init = async (room: number | string, id: string) => {
    console.log('[SIGNALR][ATTEMPTS]', this.attempts);
    if (!this.connection) {
      console.log('[SIGNALR] => init', room);

      const origin = process.env.REACT_APP_SIGNALR as string;

      this.connection = new HubConnectionBuilder()
        .withUrl(`${origin}?userId=${id}&room=${room}`)
        .withServerTimeout(60000)
        .withKeepAliveInterval(15000)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: retryContext => {
            const delays = [
              1000, 1000, 2000, 2000, 3000, 3000, 3000, 3000, 3000, 3000
            ];
            return delays[retryContext.previousRetryCount] ?? null;
          }
        })
        .configureLogging(LogLevel.Information)
        .build();

      this.connection.onreconnecting(e => {
        console.log(`[SIGNALR] => Reconnecting ${new Date().toISOString()}`, e);
        trackSignalR('[SIGNALR] => Reconnecting', e?.message);
      });

      this.connection.onreconnected(connectionId => {
        console.log(
          `[SIGNALR][RECONNECTED] ${new Date().toISOString()}`,
          connectionId
        );
        trackSignalR('[SIGNALR][RECONNECTED]', connectionId);

        if (!this.pingInterval) {
          this.pingInterval = setInterval(() => {
            this.emit(SIGNALR_EVENTS.PING);
          }, 20000);
        }
      });

      this.connection.onclose(e => {
        console.error(`[SIGNALR][DISCONNECTED] ${new Date().toISOString()}`, e);
        trackSignalR('[SIGNALR][DISCONNECTED]', e?.message);
        // this.connection = undefined;

        if (this.pingInterval) {
          clearInterval(this.pingInterval);
          this.pingInterval = undefined;
        }

        // if (this.attempts < MAX_ATTEMPTS) {
        //   console.log('[SIGNALR] => waiting for reconnect');
        //   setTimeout(() => {
        //     this.attempts += 1;
        //     this.init(port, id);
        //   }, 5000);
        // }
      });

      await this.connect();
    }
  };

  connect = async () => {
    if (this.connection) {
      console.log('[SIGNALR] => Connect');
      try {
        await this.connection.start();
        console.log('[SIGNALR][CONNECTED]');
        trackSignalR('[SIGNALR][CONNECTED]');
        if (!this.pingInterval) {
          this.pingInterval = setInterval(() => {
            this.emit(SIGNALR_EVENTS.PING);
          }, 20000);
        }
      } catch (e: any) {
        console.log('[SIGNALR][CONNECT FAILED]', e);
        trackSignalR('[SIGNALR][CONNECT FAILED]', e?.message);
      }
    }
  };

  on = (cb?: (data: ISocketMessageParsedData) => void) => {
    if (this.connection) {
      const _d = (d: string): ISocketMessageParsedData => {
        return JSON.parse(d);
      };

      this.connection.on('Broadcast', d => {
        if (d) {
          cb?.(_d(d));
          trackSignalR('[SIGNALR] => Broadcast', JSON.stringify(_d(d)));
        }
      });

      this.connection.on('Send', d => {
        if (d) {
          cb?.(_d(d));
          trackSignalR('[SIGNALR] => Send', JSON.stringify(_d(d)));
        }
      });
    }
  };

  log = (tag: string, event: string, data?: any) => {
    const ignorePatterns: Array<string> = [SIGNALR_EVENTS.PING];
    if (!ignorePatterns.includes(event)) {
      console.log(tag, event, data);
    }
  };

  emit = async (ev: string, data?: any) => {
    if (
      this.connection &&
      this.connection.state === HubConnectionState.Connected
    ) {
      this.log('[SIGNALR] => Emit', ev, data);

      try {
        const jsonData = { ...data, type: ev };
        this.log('[SIGNALR] => SendMessage', ev, JSON.stringify(jsonData));
        trackSignalR(`[SIGNALR] => Emit ${ev}`, JSON.stringify(jsonData));
        await this.connection.send('SendMessage', JSON.stringify(jsonData));
        this.log('[SIGNALR] => Emit success', ev);
        trackSignalR(`[SIGNALR][EMITTED] => ${ev}`);
      } catch (e: any) {
        console.log('[SIGNALR][EMIT FAILED]', e);
        trackSignalR(`[SIGNALR][EMIT FAILED] => ${ev}`, e?.message);
      }
    }
  };

  emitWithLoading = async (ev: string, data?: any) => {
    if (
      this.connection &&
      this.connection.state === HubConnectionState.Connected
    ) {
      this.log('[SIGNALR] => Emit', ev, data);
      try {
        const jsonData = { ...data, type: ev };
        await this.connection.invoke('SendMessage', JSON.stringify(jsonData));
      } catch (e) {
        console.log('[SIGNALR][EMIT FAILED]', e);
      }
    }
  };

  disconnect = async () => {
    console.log('[SIGNALR] => disconnect');
    this.attempts = MAX_ATTEMPTS;
    // if (this.pingInterval) {
    //   clearInterval(this.pingInterval);
    //   this.pingInterval = undefined;
    // }
    if (this.connection) {
      await this.connection.stop();
      this.connection = undefined;

      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = undefined;
      }
    }
  };
}

export default new AppSignalR();

export enum SIGNALR_EVENTS {
  PING = 'PING',
  PONG = 'PONG',
  JOIN_ROOM = 'JOIN_ROOM',
  DISCONNECT = 'DISCONNECT',
  UPDATE_ROOM_SETTINGS = 'UPDATE_ROOM_SETTINGS', // Waiting Room only
  UPDATE_SCORE = 'UPDATE_SCORE',
  SET_FINAL_SCORE = 'SET_FINAL_SCORE',
  UPDATE_ROOM_STATUS = 'UPDATE_ROOM_STATUS',
  READY = 'READY',
  PAUSE = 'PAUSE',
  RESUME = 'RESUME',
  START_GAME = 'START_GAME',
  REMAINING_TIME = 'REMAINING_TIME',
  START_WORD = 'START_WORD',
  WORD_TIME_UP = 'WORD_TIME_UP',
  TERMINATE_ROOM = 'TERMINATE_ROOM',
  EMERGENCY_END = 'EMERGENCY_END',
  RESTART_GAME = 'RESTART_GAME',
  CHANGE_DESCRIBER = 'CHANGE_DESCRIBER',
  UPDATE_WORDS_ORDER = 'UPDATE_WORDS_ORDER', // For JUMBLE only
  PING_TIMER = 'PING_TIMER' // For interval countdown only
}
